/**
 * Utilidad para guardar y recuperar borradores de venta en localStorage.
 * Permite al usuario guardar una venta en curso, salir a crear items/productos
 * y recuperar la venta al volver.
 */

const STORAGE_KEY_PREFIX = 'saleDraft_';

/**
 * Obtiene la clave de almacenamiento para el usuario actual
 * @param {string} userId - Firebase UID del usuario
 * @returns {string}
 */
const getStorageKey = (userId) => `${STORAGE_KEY_PREFIX}${userId || 'anonymous'}`;

/**
 * Guarda un borrador de venta en localStorage
 * @param {string} userId - Firebase UID del usuario
 * @param {Object} draft - Datos del borrador
 * @param {Array} draft.selectedItems - Items seleccionados en el carrito
 * @param {string} draft.paymentMethod - Método de pago seleccionado
 * @param {string} draft.customerSearch - Búsqueda de cliente
 * @param {Object|null} draft.selectedCustomer - Cliente seleccionado
 * @param {Object} draft.installmentData - Datos de abono si aplica
 * @param {number} draft.uniqueKey - Último key usado para items (para continuar la secuencia)
 */
export const saveSaleDraft = (userId, draft) => {
    try {
        const key = getStorageKey(userId);
        const data = {
            ...draft,
            savedAt: new Date().toISOString(),
        };
        localStorage.setItem(key, JSON.stringify(data));
        return true;
    } catch (e) {
        console.error('Error guardando borrador de venta:', e);
        return false;
    }
};

/**
 * Recupera el borrador de venta del usuario
 * @param {string} userId - Firebase UID del usuario
 * @returns {Object|null} Borrador o null si no existe
 */
export const getSaleDraft = (userId) => {
    try {
        const key = getStorageKey(userId);
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (e) {
        console.error('Error recuperando borrador de venta:', e);
        return null;
    }
};

/**
 * Elimina el borrador de venta del usuario
 * @param {string} userId - Firebase UID del usuario
 */
export const clearSaleDraft = (userId) => {
    try {
        const key = getStorageKey(userId);
        localStorage.removeItem(key);
    } catch (e) {
        console.error('Error eliminando borrador de venta:', e);
    }
};

/**
 * Verifica si existe un borrador guardado
 * @param {string} userId - Firebase UID del usuario
 * @returns {boolean}
 */
export const hasSaleDraft = (userId) => {
    const draft = getSaleDraft(userId);
    return draft != null && Array.isArray(draft.selectedItems) && draft.selectedItems.length > 0;
};
